import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Empty } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { MessageSquare, Plus, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Messages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [reply, setReply] = useState("");
  const [form, setForm] = useState({ subject: "", message: "" });

  const load = () => api.get("/update-requests").then(({ data }) => { setThreads(data); if (active) { const a = data.find((t) => t.id === active.id); if (a) setActive(a); } });
  useEffect(() => { load(); }, []);

  const openThread = async (t) => {
    const { data } = await api.get(`/update-requests/${t.id}`);
    setActive(data);
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/update-requests", form);
      toast.success("Message sent to the team");
      setOpen(false);
      setForm({ subject: "", message: "" });
      await load();
      setActive(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    const { data } = await api.post(`/update-requests/${active.id}/messages`, { body: reply });
    setActive(data);
    setReply("");
    load();
  };

  return (
    <div>
      <PageHeader overline="Update requests & threads" title="Messages">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-message-btn" className="rounded-none bg-primary hover:bg-[#09090B]"><Plus className="h-4 w-4 mr-1" /> New</Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-black/10 rounded-none">
            <DialogHeader><DialogTitle className="font-heading">Message the team</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="space-y-2"><Label>Subject</Label><Input data-testid="msg-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required className="bg-[#F4F4F5] border-black/10 rounded-none" /></div>
              <div className="space-y-2"><Label>Message</Label><Textarea data-testid="msg-body" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required className="bg-[#F4F4F5] border-black/10 rounded-none min-h-[100px]" /></div>
              <DialogFooter><Button type="submit" data-testid="msg-send" className="rounded-none bg-primary hover:bg-[#09090B]">Send</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="p-8">
        {!threads && <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
        {threads && threads.length === 0 && <Empty icon={MessageSquare} title="No messages yet" hint="Send an update request to the internal team and track replies here." />}
        {threads && threads.length > 0 && (
          <div className="grid lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
            <div className="bg-white max-h-[70vh] overflow-y-auto">
              {threads.map((t) => (
                <button key={t.id} onClick={() => openThread(t)} className={`w-full text-left p-4 border-b border-black/5 hover:bg-black/5 transition-colors ${active?.id === t.id ? "bg-black/5" : ""}`}>
                  <p className="text-sm font-medium truncate">{t.subject}</p>
                  <p className="overline text-[0.5rem] mt-1">{t.status} · {t.messages?.length || 0} messages</p>
                </button>
              ))}
            </div>
            <div className="lg:col-span-2 bg-white p-6 flex flex-col min-h-[50vh]">
              {!active ? <p className="text-sm text-muted-foreground">Select a thread.</p> : (
                <>
                  <p className="font-heading font-bold text-lg mb-4">{active.subject}</p>
                  <div className="flex-1 space-y-3 overflow-y-auto">
                    {active.messages?.map((m, i) => {
                      const mine = m.sender_role === user?.role;
                      return (
                        <div key={i} className={`max-w-[80%] p-3 text-sm ${mine ? "ml-auto bg-primary text-white" : "bg-[#F1F1F3]"}`}>
                          <p className="overline text-[0.5rem] mb-1" style={{ color: mine ? "rgba(255,255,255,0.7)" : undefined }}>{m.sender_name || m.sender_role}</p>
                          {m.body}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 mt-4 border-t border-black/10 pt-4">
                    <Input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} placeholder="Type a reply…" className="bg-[#F4F4F5] border-black/10 rounded-none" data-testid="reply-input" />
                    <Button onClick={sendReply} data-testid="reply-send" className="rounded-none bg-primary hover:bg-[#09090B]"><Send className="h-4 w-4" /></Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
